import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ChildrenProps } from '@dfl/mui-react-common';

interface ExportModalContextValue {
  isOpen: boolean;
  currentExportId: string | undefined;
  openModal: (exportId?: string) => void;
  closeModal: () => void;
  setExportId: (exportId: string) => void;
}

const ExportModalContext = createContext<ExportModalContextValue | undefined>(undefined);

export const ExportModalProvider = ({ children }: ChildrenProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currentExportId, setCurrentExportId] = useState<string | undefined>(undefined);

  const openModal = useCallback((exportId?: string) => {
    // console.log('🟢 ExportModalProvider: Opening modal', { exportId });
    setIsOpen(true);
    if (exportId) {
      setCurrentExportId(exportId);
    }
  }, []);

  const closeModal = useCallback(() => {
    // console.log('🔴 ExportModalProvider: Closing modal and resetting state');
    setIsOpen(false);
    setCurrentExportId(undefined);
  }, []);

  const setExportId = useCallback((exportId: string) => {
    // console.log('🔵 ExportModalProvider: Setting export ID', { exportId });
    setCurrentExportId(exportId);
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      currentExportId,
      openModal,
      closeModal,
      setExportId,
    }),
    [isOpen, currentExportId, openModal, closeModal, setExportId],
  );

  return <ExportModalContext.Provider value={value}>{children}</ExportModalContext.Provider>;
};

export const useExportModal = (): ExportModalContextValue => {
  const context = useContext(ExportModalContext);
  if (!context) {
    throw new Error('useExportModal must be used within ExportModalProvider');
  }
  return context;
};
